import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  Globe,
  Shield,
  Key,
  Mail,
  Check,
  X,
  Copy,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Info,
  Trash2,
  Plus,
  ExternalLink,
  Lock,
  Users,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { API_URL, BACKEND_URL } from '../../config';

export default function CustomDomainSettings({ organizationId, spaceId, spaceName }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [domainConfig, setDomainConfig] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [password, setPassword] = useState('');
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchDomainConfig = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/custom-domain`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDomainConfig(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch domain config:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, spaceId, token]);

  useEffect(() => {
    fetchDomainConfig();
  }, [fetchDomainConfig]);

  const handleSetupDomain = async () => {
    if (!newDomain.trim()) {
      toast.error('Please enter a domain');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/custom-domain`,
        { domain: newDomain.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDomainConfig(response.data);
      setShowSetup(false);
      setNewDomain('');
      toast.success('Domain configured! Now set up your DNS records.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to configure domain');
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyDomain = async () => {
    setVerifying(true);
    try {
      const response = await axios.post(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/custom-domain/verify`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.verified) {
        setDomainConfig(prev => ({ ...prev, is_verified: true }));
        toast.success('Domain verified successfully!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleUpdateAccessType = async (accessType) => {
    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/custom-domain`,
        { access_type: accessType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDomainConfig(response.data);
      toast.success('Access settings updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSetPassword = async () => {
    if (!password.trim()) {
      toast.error('Please enter a password');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/custom-domain`,
        { password: password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDomainConfig(response.data);
      setPassword('');
      toast.success('Password set successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setSaving(false);
    }
  };

  const handleSendInvite = async () => {
    if (!newInviteEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }

    setSendingInvite(true);
    try {
      const response = await axios.post(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/custom-domain/invites`,
        { email: newInviteEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDomainConfig(prev => ({
        ...prev,
        invites: [...(prev.invites || []), response.data]
      }));
      setNewInviteEmail('');
      toast.success('Invite sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await axios.delete(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/custom-domain/invites/${inviteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDomainConfig(prev => ({
        ...prev,
        invites: prev.invites.filter(i => i.id !== inviteId)
      }));
      toast.success('Invite revoked');
    } catch (error) {
      toast.error('Failed to revoke invite');
    }
  };

  const handleRemoveDomain = async () => {
    try {
      await axios.delete(
        `${API_URL}/organizations/${organizationId}/spaces/${spaceId}/custom-domain`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDomainConfig(null);
      toast.success('Custom domain removed');
    } catch (error) {
      toast.error('Failed to remove domain');
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="py-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
      </div>
    );
  }

  if (!domainConfig) {
    return (
      <Card data-testid="custom-domain-setup">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Custom Domain
          </CardTitle>
          <CardDescription>
            Link your own domain to this documentation (e.g., help.yourdomain.com)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowSetup(true)} data-testid="setup-domain-btn">
            <Plus className="w-4 h-4 mr-2" />
            Set Up Custom Domain
          </Button>

          <Dialog open={showSetup} onOpenChange={setShowSetup}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Set Up Custom Domain</DialogTitle>
                <DialogDescription>
                  Enter the domain you want to use for this documentation.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Domain</Label>
                  <Input
                    placeholder="help.yourdomain.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    data-testid="domain-input"
                  />
                  <p className="text-xs text-slate-500">
                    Use a subdomain like help., docs., or support.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowSetup(false)}>Cancel</Button>
                <Button onClick={handleSetupDomain} disabled={saving}>
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Continue
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }

  const docsScheme =
    domainConfig.docs_url_scheme === 'http' || domainConfig.docs_url_scheme === 'https'
      ? domainConfig.docs_url_scheme
      : (() => {
          try {
            return new URL(BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : '')).protocol ===
              'http:'
              ? 'http'
              : 'https';
          } catch {
            return 'https';
          }
        })();
  const docsPublicUrl = `${docsScheme}://${domainConfig.domain}`;

  return (
    <div className="space-y-6" data-testid="custom-domain-settings">
      {/* Domain Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Custom Domain
              </CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                <span className="font-mono text-sm">{domainConfig.domain}</span>
                {domainConfig.is_verified ? (
                  <Badge className="bg-green-100 text-green-700">
                    <Check className="w-3 h-3 mr-1" /> Verified
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    <AlertTriangle className="w-3 h-3 mr-1" /> Not Verified
                  </Badge>
                )}
              </CardDescription>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove Custom Domain?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove the custom domain. Visitors will no longer be able to access your docs via {domainConfig.domain}.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRemoveDomain} className="bg-red-600 hover:bg-red-700">
                    Remove Domain
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardHeader>

        {domainConfig.is_verified && (
          <CardContent className="pt-0">
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-green-200 dark:border-green-900 bg-green-50/80 dark:bg-green-950/30 px-3 py-2 text-sm text-green-900 dark:text-green-100">
              <Check className="w-4 h-4 shrink-0" />
              <span className="font-medium">Public documentation:</span>
              <a
                href={docsPublicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-mono text-green-800 dark:text-green-200 underline underline-offset-2 hover:opacity-90"
              >
                {docsPublicUrl}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </CardContent>
        )}

        {!domainConfig.is_verified && (
          <CardContent className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <h4 className="font-medium text-amber-800 dark:text-amber-200 flex items-center gap-2 mb-2">
                <Info className="w-4 h-4" />
                DNS Configuration Required
              </h4>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                After DNS propagates, visitors open{' '}
                <span className="font-mono font-medium">{docsPublicUrl}</span>
                — the same published documentation for this space, on your own hostname. Your
                subdomain must resolve to the <strong>same servers</strong> as your DocTree deployment
                (the value below).
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                In your DNS provider, add a <strong>CNAME</strong> from your subdomain to this target:
              </p>
              <div className="bg-white dark:bg-slate-900 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500">Type</div>
                    <div className="font-mono text-sm">CNAME</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Name/Host</div>
                    <div className="font-mono text-sm">{domainConfig.domain.split('.')[0]}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Value/Target</div>
                    <div className="font-mono text-sm flex items-center gap-2">
                      {domainConfig.cname_target}
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => copyToClipboard(domainConfig.cname_target, 'cname')}
                      >
                        {copied === 'cname' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <Button onClick={handleVerifyDomain} disabled={verifying} className="w-full">
              {verifying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Verify DNS Configuration
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Access Control Card */}
      {domainConfig.is_verified && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Access Control
            </CardTitle>
            <CardDescription>
              Control who can access your documentation via {domainConfig.domain}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <Label>Access Type</Label>
              <Select 
                value={domainConfig.access_type} 
                onValueChange={handleUpdateAccessType}
                disabled={saving}
              >
                <SelectTrigger data-testid="access-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-green-500" />
                      <div>
                        <div className="font-medium">Public</div>
                        <div className="text-xs text-slate-500">Anyone can access</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="password">
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-amber-500" />
                      <div>
                        <div className="font-medium">Password Protected</div>
                        <div className="text-xs text-slate-500">Require password to access</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="invite">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-500" />
                      <div>
                        <div className="font-medium">Invite Only</div>
                        <div className="text-xs text-slate-500">Only invited users can access</div>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Password Protection Section */}
            {domainConfig.access_type === 'password' && (
              <div className="space-y-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-amber-600" />
                  <Label>Password Protection</Label>
                  {domainConfig.has_password && (
                    <Badge variant="outline" className="text-green-600 border-green-300">
                      <Check className="w-3 h-3 mr-1" /> Set
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    placeholder={domainConfig.has_password ? "Enter new password to change" : "Set a password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="domain-password-input"
                  />
                  <Button onClick={handleSetPassword} disabled={saving || !password.trim()}>
                    {domainConfig.has_password ? 'Update' : 'Set'} Password
                  </Button>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Visitors will need to enter this password to access the documentation.
                </p>
              </div>
            )}

            {/* Invite Only Section */}
            {domainConfig.access_type === 'invite' && (
              <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-600" />
                  <Label>Invite Users</Label>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={newInviteEmail}
                    onChange={(e) => setNewInviteEmail(e.target.value)}
                    data-testid="invite-email-input"
                  />
                  <Button onClick={handleSendInvite} disabled={sendingInvite || !newInviteEmail.trim()}>
                    {sendingInvite && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Send Invite
                  </Button>
                </div>

                {/* Invites List */}
                {domainConfig.invites?.length > 0 && (
                  <div className="space-y-2 mt-4">
                    <Label className="text-sm text-slate-600">Invited Users</Label>
                    <div className="space-y-2">
                      {domainConfig.invites.map((invite) => (
                        <div 
                          key={invite.id} 
                          className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded-lg"
                        >
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span className="text-sm">{invite.email}</span>
                            <Badge variant={invite.status === 'accepted' ? 'default' : 'outline'} className="text-xs">
                              {invite.status}
                            </Badge>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => handleRevokeInvite(invite.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Users will receive an email with a unique access link.
                </p>
              </div>
            )}

            {/* Public Access Note */}
            {domainConfig.access_type === 'public' && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Anyone with the link can access your documentation.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview Link */}
      {domainConfig.is_verified && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Your Documentation URL</div>
                <div className="font-mono text-sm text-slate-500">{docsPublicUrl}</div>
              </div>
              <Button variant="outline" asChild>
                <a href={docsPublicUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Visit
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
